/**
 * Approval-request decision logic (DM-6437).
 *
 * Split out for the same reason as broadcasts.logic.ts: apps/admin-ui is never
 * ESLint'd or complexity-checked in CI and its jest suite matches `.ts` only,
 * so pure logic in a sibling file is the one part of the flow that can be
 * tested here. Everything that decides what a viewer may do, when a request
 * has silently expired, and how often to poll lives here.
 */

import type {
  ApprovalRequest,
  ApprovalRequestStatus,
  DestructiveOperation,
} from '../api/approvalRequests';
import type { ReasonOption } from '../components/ui/ReasonActionBar';
import type { ChipTone } from '../components/ui/StatusChip';

/** A request moves from PENDING to APPROVED to COMPLETED within minutes, so poll fast and stop early. */
export const APPROVAL_POLL_MS = 5000;

/** Sidebar count only needs to be roughly right. */
export const PENDING_COUNT_POLL_MS = 60000;

const TERMINAL: readonly ApprovalRequestStatus[] = [
  'REJECTED',
  'WITHDRAWN',
  'COMPLETED',
  'EXPIRED',
];

/**
 * The reason travels as one header. The enumerated reason comes first so the
 * audit ledger keeps a bounded value; the free-text note rides behind it.
 */
export function buildReasonHeader(reason: string, note?: string): string {
  const trimmed = note?.trim();
  return trimmed ? `${reason}: ${trimmed}` : reason;
}

/**
 * The server only rewrites a status when something touches the row, so a
 * request that nobody decided, or a token nobody used, still reads as
 * PENDING / APPROVED in the API. The console must not offer a decision on a
 * dead request or replay a dead token, hence the time-aware view.
 */
export function effectiveStatus(request: ApprovalRequest, nowIso: string): ApprovalRequestStatus {
  const now = Date.parse(nowIso);
  if (request.status === 'PENDING' && Date.parse(request.expires_at) <= now) {
    return 'EXPIRED';
  }
  if (
    request.status === 'APPROVED' &&
    request.approval_expires_at !== undefined &&
    Date.parse(request.approval_expires_at) <= now
  ) {
    return 'EXPIRED';
  }
  return request.status;
}

export function isTerminal(status: ApprovalRequestStatus): boolean {
  return TERMINAL.includes(status);
}

export interface DecisionGate {
  canDecide: boolean;
  note: string;
}

/**
 * What this viewer may do with a request. Mirrors the server's rules so the
 * operator sees why the decision bar is missing instead of a bare 403.
 */
export function canDecide({
  request,
  viewerSub,
  viewerIsOperator,
  nowIso,
}: {
  request: ApprovalRequest;
  viewerSub: string | undefined;
  viewerIsOperator: boolean;
  nowIso: string;
}): DecisionGate {
  const status = effectiveStatus(request, nowIso);
  if (status === 'APPROVED') {
    return {
      canDecide: false,
      note: 'Already approved. The requester completes the action from their own console.',
    };
  }
  if (status !== 'PENDING') {
    return { canDecide: false, note: 'This request is no longer pending.' };
  }
  if (viewerSub !== undefined && request.requester_id === viewerSub) {
    return {
      canDecide: false,
      note: 'You requested this, so a different operator has to decide it. You can withdraw it.',
    };
  }
  if (!viewerIsOperator) {
    return {
      canDecide: false,
      note: 'Deciding a request requires the admin or admin-operator role.',
    };
  }
  return {
    canDecide: true,
    note: request.revision
      ? 'Approving does not publish. It approves exactly the content shown below, and the requester can publish it from their console within 7 days. If the live route changes first, the approval is void.'
      : 'Approving does not run the action. It lets the requester complete it from their console within 15 minutes.',
  };
}

const OPERATION_LABELS: Record<
  DestructiveOperation,
  { label: string; verb: string; tone: ChipTone }
> = {
  publishContent: { label: 'Publish content', verb: 'Publish', tone: 'warn' },
  publishContentRevision: { label: 'Publish content revision', verb: 'Publish', tone: 'warn' },
  deleteContent: { label: 'Delete content', verb: 'Delete', tone: 'crit' },
  activateExperiment: { label: 'Activate experiment', verb: 'Activate', tone: 'info' },
  archiveExperiment: { label: 'Archive experiment', verb: 'Archive', tone: 'warn' },
  deleteCdnFile: { label: 'Delete CDN file', verb: 'Delete', tone: 'crit' },
};

function isDestructiveOperation(operation: string): operation is DestructiveOperation {
  return operation in OPERATION_LABELS;
}

/** Label and tone for an operation; unknown operations still render honestly. */
export function describeOperation(operation: string): {
  label: string;
  verb: string;
  tone: ChipTone;
} {
  return isDestructiveOperation(operation)
    ? OPERATION_LABELS[operation]
    : { label: operation, verb: 'Run', tone: 'neutral' };
}

const STATUS_CHIPS: Record<ApprovalRequestStatus, { label: string; tone: ChipTone }> = {
  PENDING: { label: 'Awaiting approval', tone: 'warn' },
  APPROVED: { label: 'Approved', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral' },
  COMPLETED: { label: 'Completed', tone: 'ok' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
};

export function statusChip(status: ApprovalRequestStatus): { label: string; tone: ChipTone } {
  return STATUS_CHIPS[status] ?? { label: status, tone: 'neutral' };
}

/** How often a single request should be polled, or false once nothing can change. */
export function pollInterval(status: ApprovalRequestStatus | undefined): number | false {
  if (status === undefined || isTerminal(status)) return false;
  return APPROVAL_POLL_MS;
}

/** How often a list should be polled: only while something in it is still moving. */
export function listPollInterval(
  requests: readonly ApprovalRequest[] | undefined,
  nowIso: string
): number | false {
  if (!requests) return false;
  return requests.some(request => !isTerminal(effectiveStatus(request, nowIso)))
    ? APPROVAL_POLL_MS
    : false;
}

/**
 * Resume an open request instead of opening a duplicate: a requester who
 * closed the modal and came back should land on the same wait screen, and an
 * approved-but-not-completed request should complete rather than re-ask.
 */
export function findOpenRequest(
  requests: readonly ApprovalRequest[],
  match: { operation: string; target_id: string; requester_id: string },
  nowIso: string
): ApprovalRequest | null {
  const open = requests
    .filter(
      request =>
        request.operation === match.operation &&
        request.target_id === match.target_id &&
        request.requester_id === match.requester_id &&
        !isTerminal(effectiveStatus(request, nowIso))
    )
    .sort((left, right) => Date.parse(right.requested_at) - Date.parse(left.requested_at));
  return open[0] ?? null;
}

/** Countdown chip for whichever deadline currently applies, or null when none does. */
export function expiryCountdown(
  request: ApprovalRequest,
  nowIso: string
): { label: string; tone: ChipTone } | null {
  const status = effectiveStatus(request, nowIso);
  if (status === 'EXPIRED') return { label: 'Expired', tone: 'crit' };
  const deadline =
    status === 'PENDING'
      ? request.expires_at
      : status === 'APPROVED'
        ? request.approval_expires_at
        : undefined;
  if (deadline === undefined) return null;
  const msLeft = Date.parse(deadline) - Date.parse(nowIso);
  if (msLeft <= 0) return { label: 'Expired', tone: 'crit' };
  const minutesLeft = Math.max(1, Math.floor(msLeft / 60_000));
  if (minutesLeft < 60)
    return { label: `${minutesLeft}m left`, tone: minutesLeft < 5 ? 'warn' : 'neutral' };
  const hoursLeft = Math.floor(minutesLeft / 60);
  return { label: `${hoursLeft}h left`, tone: hoursLeft < 6 ? 'warn' : 'neutral' };
}

/**
 * Enumerated reasons per operation. Free text stays in the note so the audit
 * ledger keeps a bounded value, the same rule every other console action
 * follows.
 */
export const REASONS_BY_OPERATION: Record<DestructiveOperation, ReasonOption[]> = {
  publishContent: [
    { value: 'CONTENT_RELEASE', label: 'Content release' },
    { value: 'FORCED_UPDATE_GATE', label: 'Forced-update gate change (app-version)' },
    { value: 'HOTFIX', label: 'Hotfix' },
    { value: 'ROLLBACK', label: 'Rollback to a previous version' },
    { value: 'OTHER', label: 'Other (explain in the note)' },
  ],
  publishContentRevision: [
    { value: 'CONTENT_RELEASE', label: 'Content release' },
    { value: 'FORCED_UPDATE_GATE', label: 'Forced-update gate change (app-version)' },
    { value: 'HOTFIX', label: 'Hotfix' },
    { value: 'ROLLBACK', label: 'Rollback to a previous version' },
    { value: 'OTHER', label: 'Other (explain in the note)' },
  ],
  deleteContent: [
    { value: 'CLEANUP', label: 'Cleanup of an unused route' },
    { value: 'ROLLBACK', label: 'Rollback: take the route offline' },
    { value: 'OTHER', label: 'Other (explain in the note)' },
  ],
  activateExperiment: [
    { value: 'EXPERIMENT_ROLLOUT', label: 'Experiment rollout' },
    { value: 'OTHER', label: 'Other (explain in the note)' },
  ],
  archiveExperiment: [
    { value: 'EXPERIMENT_END', label: 'Experiment concluded' },
    { value: 'ROLLBACK', label: 'Rollback: stop the experiment' },
    { value: 'OTHER', label: 'Other (explain in the note)' },
  ],
  deleteCdnFile: [
    { value: 'FILE_CLEANUP', label: 'File cleanup' },
    { value: 'ROLLBACK', label: 'Rollback: remove a bad upload' },
    { value: 'OTHER', label: 'Other (explain in the note)' },
  ],
};

export const DECISION_REASONS: ReasonOption[] = [
  { value: 'approved', label: 'Approve: let the requester complete this action' },
  { value: 'rejected', label: 'Reject: do not allow this action' },
];
